import { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLang } from "@/lib/i18n";
import { useSeoMeta } from "@/lib/useSeoMeta";
import { articlesFor, findArticle, guidesBase, articleUrl, articleAlternates } from "@/content/articles";
import { getPage, pageUrl, alternatesFor, ORIGIN } from "@/content/seo";
import { orgAndWebSite, articleBlock, breadcrumbFromPath, formatUpdatedFR, CONTENT_LAST_MODIFIED, DIRECTIVE_APPLICABLE_SINCE } from "@/lib/schemaOrg";
import { ArrowRight, Clock, BookOpen, CalendarClock, Youtube, PlayCircle } from "lucide-react";

/** Adapter minimaliste — délègue à useSeoMeta (un script JSON-LD par bloc). */
function useSeo({ title, description, jsonLd, lang, canonical, keywords, alternates }) {
  useEffect(() => { if (lang) document.documentElement.lang = lang; }, [lang]);
  useSeoMeta({ title, description, keywords, canonical, alternates, jsonLd });
}

export default function GuidesIndex({ lang: routeLang }) {
  const { lang, setLang, t } = useLang();
  useEffect(() => { if (routeLang && routeLang !== lang) setLang(routeLang); }, [routeLang, lang, setLang]);
  const activeLang = routeLang || lang;
  const list = articlesFor(activeLang);
  const meta = getPage("guides", activeLang === "en" ? "fr" : activeLang);
  const canonical = activeLang === "en" ? `${ORIGIN}/guides` : pageUrl("guides", activeLang);
  useSeo({
    title: meta.title, description: meta.description, keywords: meta.keywords, lang: activeLang, canonical,
    alternates: alternatesFor("guides"),
    jsonLd: { "@context": "https://schema.org", "@type": "CollectionPage", name: t("guides.title"), inLanguage: activeLang,
      hasPart: list.map((a) => ({ "@type": "Article", headline: a.title, url: ORIGIN + articleUrl(a), datePublished: a.date })) },
  });
  return (
    <div className="App min-h-screen">
      <Header variant="public" />
      <section className="brand-mesh border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-20">
          <p className="eyebrow mb-3">{t("guides.eyebrow")}</p>
          <h1 className="text-4xl md:text-5xl tracking-tight max-w-3xl" data-testid="guides-title">{t("guides.title")}</h1>
          <p className="mt-5 text-muted-foreground max-w-2xl leading-relaxed">{t("guides.sub")}</p>
        </div>
      </section>
      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="guides-list">
        {list.map((a, i) => (
          <Link key={a.slug} to={articleUrl(a)} className={`hairline card-lift p-7 flex flex-col fade-up delay-${(i % 4) + 1}`} data-testid={`guide-card-${a.slug}`}>
            <div className="flex items-center justify-between mb-5">
              <span className="w-9 h-9 rounded-lg bg-accent text-primary flex items-center justify-center"><BookOpen className="w-4 h-4" /></span>
              <span className="font-mono text-[11px] text-muted-foreground inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {a.readMin} min</span>
            </div>
            <h2 className="text-lg leading-snug mb-3">{a.title}</h2>
            <p className="text-sm text-muted-foreground leading-relaxed flex-1">{a.excerpt}</p>
            <span className="mt-5 text-sm font-medium text-primary inline-flex items-center gap-1">{t("guides.read")} <ArrowRight className="w-4 h-4" /></span>
          </Link>
        ))}
      </main>

      {/* Encart YouTube (GEO Lot 2 - amplification E-E-A-T) */}
      <section className="border-t border-border bg-white" data-testid="guides-youtube-card">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 md:py-16">
          <div className="rounded-2xl overflow-hidden relative bg-gradient-to-br from-primary via-primary to-[#1F5A99] text-white p-8 md:p-12 grid md:grid-cols-12 gap-8 items-center">
            <div className="absolute -right-16 -bottom-16 w-[380px] h-[380px] rounded-full border-[24px] border-white/5 pointer-events-none" />
            <div className="absolute right-40 -top-24 w-[220px] h-[220px] rounded-full border-[18px] border-white/5 pointer-events-none" />
            <div className="md:col-span-7 relative">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-3 py-1.5 rounded-full mb-5">
                <Youtube className="w-3.5 h-3.5" />
                <span className="font-mono text-[11px] uppercase tracking-widest">Vidéo · Décryptage</span>
              </div>
              <h2 className="text-2xl md:text-4xl font-serif leading-tight mb-4">
                Suivez la chaîne YouTube EmpCo Compliance
              </h2>
              <p className="text-white/80 leading-relaxed mb-6 max-w-xl">
                Chaque semaine, une nouvelle vidéo pour décrypter la directive (UE)&nbsp;2024/825 : décisions DGCCRF commentées, reformulations d'allégations, cas pratiques par secteur et calendrier des contrôles.
              </p>
              <a
                href="https://www.youtube.com/@EMPCOCOMPLIANCE"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="guides-youtube-cta"
                className="inline-flex items-center gap-2 bg-white text-primary px-5 py-3 rounded-lg text-sm font-semibold hover:bg-white/90 transition-colors"
              >
                <PlayCircle className="w-4 h-4" /> Voir la chaîne @EMPCOCOMPLIANCE <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="md:col-span-5 relative flex justify-center">
              <div className="relative w-full max-w-[280px] aspect-video rounded-xl overflow-hidden bg-black/40 backdrop-blur border border-white/10 flex items-center justify-center">
                <Youtube className="w-16 h-16 text-white/90" strokeWidth={1.2} />
                <div className="absolute bottom-3 left-3 right-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/60">Nouveauté</p>
                  <p className="text-sm font-serif leading-snug">Directive UE 2024/825 en 3 minutes</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

export function GuideArticle({ lang: routeLang }) {
  const { slug } = useParams();
  const nav = useNavigate();
  const { lang, setLang, t } = useLang();
  const a = findArticle(slug);
  const origin = ORIGIN;

  useEffect(() => { if (a && a.lang !== lang) setLang(a.lang); }, [a, lang, setLang]);

  const jsonLd = a ? [
    orgAndWebSite(a.lang),
    articleBlock({
      url: origin + articleUrl(a),
      headline: a.title,
      description: a.excerpt,
      lang: a.lang,
      datePublished: a.date,
      dateModified: CONTENT_LAST_MODIFIED,
    }),
    breadcrumbFromPath(articleUrl(a), a.title, a.lang),
    { "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: a.faq.map(([q, ans]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: ans } })) },
  ] : null;

  useSeo({
    title: a ? `${a.title} — EmpCo` : "EmpCo",
    description: a?.excerpt || "",
    lang: a?.lang || routeLang || lang,
    canonical: a ? origin + articleUrl(a) : origin + "/guides",
    alternates: a ? articleAlternates(a) : undefined,
    jsonLd: jsonLd || {},
  });

  if (!a) return <div className="App min-h-screen"><Header variant="public" /><p className="p-10 text-center text-muted-foreground">404</p></div>;
  const related = articlesFor(a.lang).filter((x) => x.slug !== a.slug).slice(0, 3);

  return (
    <div className="App min-h-screen">
      <Header variant="public" />
      <article className="max-w-[1100px] mx-auto px-6 md:px-10 py-14 grid lg:grid-cols-12 gap-12" data-testid="guide-article">
        <div className="lg:col-span-8">
          <button onClick={() => nav(guidesBase(a.lang))} className="text-sm text-muted-foreground hover:text-primary mb-6 font-mono" data-testid="guide-back">← {t("guides.title")}</button>
          <p className="eyebrow mb-3">{t("guides.eyebrow")} · {new Date(a.date).toLocaleDateString(`${a.lang}-${a.lang.toUpperCase()}`)} · {a.readMin} min</p>
          <h1 className="text-3xl md:text-5xl tracking-tight leading-[1.08] mb-6" data-testid="guide-title">{a.title}</h1>
          <p className="text-xs font-mono text-muted-foreground mb-4 inline-flex items-center gap-2" data-testid="guide-freshness">
            <CalendarClock className="w-3.5 h-3.5" />
            Mis à jour le {formatUpdatedFR(CONTENT_LAST_MODIFIED)} · Applicable depuis le {formatUpdatedFR(DIRECTIVE_APPLICABLE_SINCE)}
          </p>
          <p className="text-lg text-muted-foreground leading-relaxed mb-10 border-l-4 border-secondary pl-5">{a.excerpt}</p>
          {a.sections.map(([h, p], i) => (
            <section key={i} className="mb-8">
              <h2 className="text-xl md:text-2xl mb-3">{h}</h2>
              <p className="leading-relaxed text-foreground/85">{p}</p>
            </section>
          ))}
          <section className="hairline p-7 mt-12" data-testid="guide-faq">
            <p className="eyebrow mb-4">FAQ</p>
            {a.faq.map(([q, ans], i) => (
              <details key={i} className="group border-b border-border last:border-0 py-3">
                <summary className="cursor-pointer font-medium flex justify-between items-center list-none">{q}<span className="text-primary group-open:rotate-45 transition-transform">+</span></summary>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{ans}</p>
              </details>
            ))}
          </section>

          {/* Sources officielles cliquables (GEO Lot 2) */}
          <section className="mt-10 border-l-4 border-primary/30 pl-5" data-testid="guide-sources">
            <p className="eyebrow mb-3">Textes et sources</p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><a href="https://eur-lex.europa.eu/eli/dir/2024/825/oj" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Directive (UE) 2024/825 — EUR-Lex</a></li>
              <li><a href="https://eur-lex.europa.eu/eli/dir/2005/29/oj" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Directive 2005/29/CE — EUR-Lex</a></li>
              <li><a href="https://www.legifrance.gouv.fr/codes/section_lc/LEGITEXT000006069565/LEGISCTA000032227103/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Code de la consommation, articles L.121-1 et suivants — Légifrance</a></li>
              <li><a href="https://www.economie.gouv.fr/dgccrf" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">DGCCRF</a></li>
            </ul>
          </section>
        </div>
        <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-24 self-start">
          <div className="bg-primary text-white p-7 rounded-xl">
            <img src="/brand/logo-icon.png" alt="EmpCo — registre des allégations environnementales" className="h-10 mb-4 brightness-0 invert opacity-90" />
            <p className="font-serif text-xl leading-snug mb-3">{t("brand.baseline")}</p>
            <p className="text-sm text-white/75 mb-5">{t("guides.cta.sub")}</p>
            <Link to="/" className="inline-flex items-center gap-2 bg-white text-primary px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-white/90" data-testid="guide-cta">{t("hero.cta")} <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div>
            <p className="eyebrow mb-3">{t("guides.related")}</p>
            <ul className="space-y-3">
              {related.map((r) => (
                <li key={r.slug}><Link to={articleUrl(r)} className="text-sm hover:text-primary leading-snug block">{r.title}</Link></li>
              ))}
            </ul>
          </div>
        </aside>
      </article>
      <Footer />
    </div>
  );
}
